import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CatalogResponse, QuoteResponse, SaveQuote } from "@breeze/shared";
import { fetchCatalog, fetchQuote, saveQuote } from "../api.js";

export function useQuote(
  vid: number | null
): ReturnType<typeof useQuery<QuoteResponse>> {
  return useQuery<QuoteResponse>({
    queryKey: ["quote", vid],
    queryFn: () => fetchQuote(vid as number),
    enabled: vid !== null,
  });
}

export function useCatalog(
  enabled: boolean
): ReturnType<typeof useQuery<CatalogResponse>> {
  return useQuery<CatalogResponse>({
    queryKey: ["catalog"],
    queryFn: fetchCatalog,
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useSaveQuote(vid: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveQuote) => saveQuote(vid as number, body),
    onSuccess: (data) => {
      qc.setQueryData(["quote", vid], data);
      // SSE will refresh the board card, but invalidate as a safety net.
      qc.invalidateQueries({ queryKey: ["board"] });
      // Visits tab in the open slideout reads from the cluster query.
      // Invalidate all cluster queries so the tab refetches without
      // forcing a page refresh after a quote save.
      qc.invalidateQueries({ queryKey: ["cluster"] });
    },
  });
}
